/**
 * Which product types a user may pick when creating a product.
 *
 * A tenant's company profile declares what it *sells* (physical goods, services,
 * subscriptions). Those settings legitimately narrow the type list for products
 * the tenant sells. But every business also *buys* types it never sells (a
 * services-only shop still buys physical office supplies), so a bought product
 * ('buy' or 'both') must not be limited to the sold-type set.
 *
 * Shared by the Create {{Product}} quick-create dialog; kept pure so the rule is
 * unit-tested directly rather than through Radix Select interaction.
 */
import type { ProductRole } from './types';

export const PRODUCT_TYPES = ['physical', 'service', 'subscription'] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

/** The subset of tenant product settings that governs the sellable type list. */
export interface SoldTypeSettings {
  sells_physical_products?: boolean;
  sells_services_by_project?: boolean;
  sells_services_by_time?: boolean;
  sells_subscriptions?: boolean;
}

/**
 * The product types the tenant sells, per its company profile. Falls back to ALL
 * types when nothing is configured yet (an unconfigured tenant isn't restricted).
 */
export function soldProductTypes(settings: SoldTypeSettings | undefined): ProductType[] {
  const types: ProductType[] = [];
  if (settings?.sells_physical_products) types.push('physical');
  if (settings?.sells_services_by_project || settings?.sells_services_by_time) types.push('service');
  if (settings?.sells_subscriptions) types.push('subscription');
  if (types.length === 0) return [...PRODUCT_TYPES];
  return types;
}

/**
 * The type options offered for a product of the given role: the sold-type set for
 * a purely-sold product, all types for a bought ('buy') or bought-and-sold
 * ('both') product.
 */
export function computeAvailableProductTypes(
  settings: SoldTypeSettings | undefined,
  role: ProductRole,
): ProductType[] {
  return role === 'sell' ? soldProductTypes(settings) : [...PRODUCT_TYPES];
}

/**
 * True when the Product Type list is actually narrowed: the product is (purely)
 * sold AND the tenant's profile sells only a subset of types. This is the case
 * whose tooltip points the user at Company Profile — for a bought product, or a
 * tenant that sells everything, the type list isn't restricted at all.
 */
export function isSellTypeRestricted(
  settings: SoldTypeSettings | undefined,
  role: ProductRole,
): boolean {
  return role === 'sell' && soldProductTypes(settings).length < PRODUCT_TYPES.length;
}
